/*
 * CEAS custom-section behaviours, ported from the mockup script.js.
 * Idempotent (guards via data-*-bound) and re-runs on shopify:section:load so
 * the theme editor add/remove/reorder works. Each init bails when its hook is
 * absent, so the file is harmless on pages without CEAS sections.
 */
(function () {
  'use strict';

  var prefersReduced = function () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  /* Fade-up reveal on scroll for [data-reveal]. */
  function revealInit(root) {
    var els = root.querySelectorAll('[data-reveal]:not([data-reveal-bound])');
    if (!els.length) return;
    if (prefersReduced() || !('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.setAttribute('data-reveal-bound', '');
        el.classList.add('is-revealed');
      });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -10% 0px' });
    els.forEach(function (el) {
      el.setAttribute('data-reveal-bound', '');
      io.observe(el);
    });
  }

  /* Tab groups: scoped to the tab's own tablist + section so multiple
     systems (featured tabs, watch & learn) coexist on one page. */
  function tabsInit(root) {
    var tabs = root.querySelectorAll('[data-tab]:not([data-tab-bound])');
    tabs.forEach(function (tab) {
      tab.setAttribute('data-tab-bound', '');
      tab.addEventListener('click', function () {
        var target = tab.dataset.tab;
        var tablist = tab.closest('[role="tablist"]');
        var section = tab.closest('.shopify-section') || document;
        var scopedTabs = tablist
          ? tablist.querySelectorAll('[data-tab]')
          : section.querySelectorAll('[data-tab]');
        var scopedPanels = section.querySelectorAll('[data-panel]');

        scopedTabs.forEach(function (t) {
          var active = t.dataset.tab === target;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', String(active));
          t.setAttribute('tabindex', active ? '0' : '-1');
        });
        scopedPanels.forEach(function (p) {
          var active = p.dataset.panel === target;
          p.classList.toggle('is-active', active);
          if (active) p.removeAttribute('hidden');
          else p.setAttribute('hidden', '');
        });
      });

      // WAI-ARIA tabs keyboard pattern: arrows/Home/End move focus and activate.
      tab.addEventListener('keydown', function (e) {
        var tablist = tab.closest('[role="tablist"]');
        if (!tablist) return;
        var group = Array.prototype.slice.call(tablist.querySelectorAll('[data-tab]'));
        var i = group.indexOf(tab), next = -1;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % group.length;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + group.length) % group.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = group.length - 1;
        else return;
        e.preventDefault();
        group[next].focus();
        group[next].click();
      });
    });
  }

  /* Featured equipment carousel: drag-to-scroll tracks + prev/next arrows
     driving the active panel's track. Scoped per [data-featured-section]. */
  function carouselInit(root) {
    root.querySelectorAll('[data-featured-section]:not([data-featured-bound])').forEach(function (section) {
      section.setAttribute('data-featured-bound', '');
      var tracks = section.querySelectorAll('[data-carousel-track]');

      tracks.forEach(function (track) {
        var isDown = false, startX, scrollLeft;
        track.addEventListener('mousedown', function (e) {
          isDown = true;
          track.classList.add('is-dragging');
          startX = e.pageX - track.offsetLeft;
          scrollLeft = track.scrollLeft;
        });
        track.addEventListener('mouseleave', function () {
          isDown = false;
          track.classList.remove('is-dragging');
        });
        track.addEventListener('mouseup', function () {
          isDown = false;
          track.classList.remove('is-dragging');
        });
        track.addEventListener('mousemove', function (e) {
          if (!isDown) return;
          e.preventDefault();
          var x = e.pageX - track.offsetLeft;
          track.scrollLeft = scrollLeft - (x - startX) * 1.5;
        });
      });

      var prev = section.querySelector('[data-carousel-prev]');
      var next = section.querySelector('[data-carousel-next]');
      var activeTrack = function () {
        return section.querySelector('[data-panel].is-active [data-carousel-track]') || tracks[0];
      };
      var cardWidth = function (t) {
        var card = t.querySelector('.equipment-card');
        return card ? card.offsetWidth + parseInt(getComputedStyle(t).gap || 12, 10) : 300;
      };
      if (prev) prev.addEventListener('click', function () {
        var t = activeTrack();
        if (t) t.scrollBy({ left: -cardWidth(t), behavior: 'smooth' });
      });
      if (next) next.addEventListener('click', function () {
        var t = activeTrack();
        if (t) t.scrollBy({ left: cardWidth(t), behavior: 'smooth' });
      });
    });
  }

  /* Category grid — drag-to-scroll track + prev/next arrows. */
  function categoryInit(root) {
    root.querySelectorAll('[data-category-section]:not([data-category-bound])').forEach(function (section) {
      section.setAttribute('data-category-bound', '');
      var track = section.querySelector('[data-category-track]');
      if (!track) return;

      var isDown = false, startX, scrollLeft;
      track.addEventListener('mousedown', function (e) {
        isDown = true;
        track.classList.add('is-dragging');
        startX = e.pageX - track.offsetLeft;
        scrollLeft = track.scrollLeft;
      });
      track.addEventListener('mouseleave', function () {
        isDown = false;
        track.classList.remove('is-dragging');
      });
      track.addEventListener('mouseup', function () {
        isDown = false;
        track.classList.remove('is-dragging');
      });
      track.addEventListener('mousemove', function (e) {
        if (!isDown) return;
        e.preventDefault();
        var x = e.pageX - track.offsetLeft;
        track.scrollLeft = scrollLeft - (x - startX) * 1.5;
      });

      var cardWidth = function () {
        var card = track.querySelector('.category-card');
        return card ? card.offsetWidth + parseInt(getComputedStyle(track).gap || 12, 10) : 262;
      };
      var prev = section.querySelector('[data-category-prev]');
      var next = section.querySelector('[data-category-next]');
      if (prev) prev.addEventListener('click', function () {
        track.scrollBy({ left: -cardWidth(), behavior: 'smooth' });
      });
      if (next) next.addEventListener('click', function () {
        track.scrollBy({ left: cardWidth(), behavior: 'smooth' });
      });
    });
  }

  /* Quote band — hidden by default, revealed by ?quote=1, #quote, or design mode. */
  function quoteInit(root) {
    root.querySelectorAll('[data-quote-cta-section]:not([data-quote-bound])').forEach(function (band) {
      band.setAttribute('data-quote-bound', '');
      var anchor = band.id ? '#' + band.id : '#quote';
      var reveal = function () { band.removeAttribute('hidden'); };
      var shouldReveal = function () {
        return /[?&]quote=1\b/.test(window.location.search)
          || window.location.hash === anchor
          || document.documentElement.classList.contains('shopify-design-mode');
      };
      if (shouldReveal()) reveal();
      window.addEventListener('hashchange', function () {
        if (window.location.hash === anchor) reveal();
      });
    });
  }

  /* PDP quote modal — opened by [data-quote-open]; the form is a native Shopify
     contact form (no AJAX). After a submit round-trip the snippet renders a
     [data-quote-result] marker, so we re-open the modal to show the result. */
  function quoteModalInit(root) {
    var modal = root.querySelector('[data-quote-modal]');
    if (!modal || modal.hasAttribute('data-quote-modal-bound')) return;
    modal.setAttribute('data-quote-modal-bound', '');

    var openers = document.querySelectorAll('[data-quote-open]');
    var closers = modal.querySelectorAll('[data-quote-modal-close]');
    var submitBtn = modal.querySelector('[data-quote-submit]');
    var lastTrigger = null;

    function open(e) {
      if (e) e.preventDefault();
      lastTrigger = e && e.currentTarget ? e.currentTarget : null;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      var firstInput = modal.querySelector('input, textarea');
      setTimeout(function () { if (firstInput) firstInput.focus(); }, 80);
    }
    function close() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
    }

    openers.forEach(function (btn) { btn.addEventListener('click', open); });
    closers.forEach(function (btn) { btn.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });

    if (submitBtn) {
      var form = submitBtn.closest('form');
      if (form) {
        form.addEventListener('submit', function () {
          submitBtn.disabled = true;
        });
      }
    }

    if (modal.querySelector('[data-quote-result]')) open();
  }

  /* Serial number lookup — routes to the storefront search results page. */
  function serialInit(root) {
    root.querySelectorAll('[data-serial-form]:not([data-serial-bound])').forEach(function (form) {
      form.setAttribute('data-serial-bound', '');
      var input = form.querySelector('[data-serial-input]');
      if (!input) return;
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var value = input.value.trim();
        if (!value) { input.focus(); return; }
        window.location.href = (window.routes && window.routes.search_url || '/search') + '?q=' + encodeURIComponent(value);
      });
    });
  }

  /* Process stepper: GSAP pin/scrub at >=1024px (dev-rules §6) with reduced-motion
     guard. Below 1024 (or reduced motion) the CSS keeps every step active and static. */
  function stepperInit(root) {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
    root.querySelectorAll('[data-motion="process-stepper"]:not([data-stepper-bound])').forEach(function (section) {
      section.setAttribute('data-stepper-bound', '');
      var steps = Array.prototype.slice.call(section.querySelectorAll('[data-step]'));
      var visuals = Array.prototype.slice.call(section.querySelectorAll('[data-step-visual]'));
      var stepsList = section.querySelector('.process-stepper__steps');
      if (!steps.length || !visuals.length || !stepsList) return;

      var setActive = function (idx) {
        steps.forEach(function (s, i) { s.classList.toggle('is-active', i === idx); });
        visuals.forEach(function (v, i) { v.classList.toggle('is-active', i === idx); });
      };

      gsap.matchMedia().add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', function () {
        setActive(0);
        var trigger = ScrollTrigger.create({
          trigger: stepsList,
          start: 'top center',
          end: 'bottom center',
          scrub: true,
          invalidateOnRefresh: true,
          onUpdate: function (self) {
            var idx = Math.min(steps.length - 1, Math.max(0, Math.floor(self.progress * steps.length)));
            setActive(idx);
          },
        });
        return function () { trigger.kill(); };
      });
    });
  }

  /* Hero headline reveal — free GSAP-core fade-up (no paid SplitText plugin).
     Targets the custom editorial hero ([data-motion="hero-reveal"]). Whole-element
     stagger (not per-word) so the heading's <em> accent markup stays intact.
     Content is visible by default, so a missing GSAP / reduced motion = no-op. */
  function heroRevealInit(root) {
    if (typeof gsap === 'undefined' || prefersReduced()) return;
    root.querySelectorAll('[data-motion="hero-reveal"]:not([data-hero-revealed])').forEach(function (hero) {
      hero.setAttribute('data-hero-revealed', '');
      var targets = hero.querySelectorAll('.hero__headline, .hero__actions');
      if (!targets.length) return;
      gsap.from(targets, { y: 24, autoAlpha: 0, duration: 0.7, ease: 'power3.out', stagger: 0.12 });
    });
  }

  /* Section-heading reveal — whole-heading fade on scroll. The mockup's
     initSectionHeadingReveal staggered SplitText chars; the client asked for a
     plain fade instead, so no split, no stagger, no SplitText dependency.
     Scoped to .theme-tokens so Dawn-native headings stay static; skips drawers,
     modals, header, footer and the sticky quote bar (the mockup's .section
     scope excluded them). Headings start transparent via the pre-hide rule in
     theme-tokens.css (so there's no flash before this deferred script runs);
     that rule's 2.5s keyframe reveals them on its own if this never executes,
     and is cancelled here when the fade takes over. */
  function headingRevealInit(root) {
    var headings = [];
    root.querySelectorAll('.theme-tokens h2:not([data-heading-revealed])').forEach(function (heading) {
      if (heading.closest('.drawer, .search-drawer, .quote-modal__panel, .site-footer, .header__inline-menu, [data-product-sticky]')) return;
      if (heading.classList.contains('visually-hidden')) return;
      heading.setAttribute('data-heading-revealed', 'true');
      headings.push(heading);
    });
    if (!headings.length) return;
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined' || prefersReduced()) {
      headings.forEach(function (heading) {
        heading.style.animation = 'none';
        heading.style.opacity = '1';
      });
      return;
    }
    headings.forEach(function (heading) {
      heading.style.animation = 'none';
      gsap.to(heading, {
        scrollTrigger: { trigger: heading, start: 'top 88%', once: true },
        opacity: 1,
        duration: 1.5,
        ease: 'power2.out'
      });
    });
  }

  /* product-recommendations (global.js) swaps its innerHTML when its fetch
     returns, replacing the heading that headingRevealInit already bound with a
     fresh unbound node — the pre-hide CSS would then hold it invisible until
     the fallback keyframe pops it in with no fade. Rebind on every swap. The
     scope is the section wrapper because the .theme-tokens wrapper that the
     heading selector needs sits outside the custom element. */
  function recommendationsRevealInit(root) {
    root.querySelectorAll('product-recommendations:not([data-heading-watch])').forEach(function (el) {
      el.setAttribute('data-heading-watch', '');
      var scope = el.closest('.shopify-section') || document;
      new MutationObserver(function () {
        headingRevealInit(scope);
      }).observe(el, { childList: true });
    });
  }

  /* About page video reveal — the hero's play button injects its [data-video-url]
     iframe into the [data-about-video-target] frame (a separate section), reveals
     it, hides the trigger, and scrolls it into view. Looks the target up at the
     document level since trigger and target live in different sections. */
  function videoRevealInit(root) {
    root.querySelectorAll('[data-about-video-trigger]:not([data-about-video-bound])').forEach(function (trigger) {
      trigger.setAttribute('data-about-video-bound', '');
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        var src = trigger.getAttribute('data-video-src');
        var url = trigger.getAttribute('data-video-url');
        var target = document.querySelector('[data-about-video-target]');
        if (!target || (!src && !url)) return;
        var media;
        if (src) {
          media = document.createElement('video');
          media.src = src;
          media.controls = true;
          media.autoplay = true;
          media.setAttribute('playsinline', '');
        } else {
          media = document.createElement('iframe');
          media.src = url;
          media.title = trigger.getAttribute('aria-label') || 'Video';
          media.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
          media.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
          media.setAttribute('allowfullscreen', '');
        }
        target.innerHTML = '';
        target.appendChild(media);
        target.classList.add('is-active');
        trigger.classList.add('is-hidden');
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
  }

  /* Inline video embed (video-spotlight) — the play button swaps the poster
     for the YouTube iframe, so no third-party embed loads before a user
     gesture. Reduced-motion users get the embed without autoplay. */
  function videoEmbedInit(root) {
    root.querySelectorAll('[data-video-embed]:not([data-video-embed-bound])').forEach(function (frame) {
      frame.setAttribute('data-video-embed-bound', '');
      var trigger = frame.querySelector('[data-video-embed-play]');
      if (!trigger) return;
      trigger.addEventListener('click', function () {
        var id = frame.getAttribute('data-video-id');
        if (!id) return;
        var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        var iframe = document.createElement('iframe');
        iframe.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) +
          '?rel=0&modestbranding=1&playsinline=1' + (reduced ? '' : '&autoplay=1');
        iframe.title = frame.getAttribute('data-video-title') || 'Video';
        iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
        iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
        iframe.setAttribute('allowfullscreen', '');
        frame.innerHTML = '';
        frame.appendChild(iframe);
        frame.classList.add('is-playing');
        iframe.focus();
      });
    });
  }

  /* Footer link columns collapse into accordions below 768px; the buttons are
     forced open + inert at >=768 via CSS, so the click handler bails there. */
  function footerAccordionInit(root) {
    var triggers = root.querySelectorAll('[data-footer-accordion-trigger]:not([data-footer-accordion-bound])');
    if (!triggers.length) return;
    triggers.forEach(function (trigger) {
      trigger.setAttribute('data-footer-accordion-bound', '');
      var id = trigger.getAttribute('data-footer-accordion-trigger');
      var panel = root.querySelector('[data-footer-accordion-panel="' + id + '"]');
      if (!panel) return;
      trigger.addEventListener('click', function () {
        if (window.innerWidth >= 768) return;
        var isOpen = panel.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', String(isOpen));
      });
    });
  }

  /* Footer newsletter drawer: opened by the Subscribe button, with a focus trap
     and focus restored to the trigger on close. Wired to a real customer form so
     submits post to Shopify. */
  function footerDrawerInit(root) {
    var drawer = root.querySelector('[data-footer-drawer]:not([data-footer-drawer-bound])');
    if (!drawer) return;
    var overlay = root.querySelector('[data-footer-drawer-overlay]');
    var openBtn = root.querySelector('[data-footer-subscribe-open]');
    var closeBtn = drawer.querySelector('[data-footer-drawer-close]');
    drawer.setAttribute('data-footer-drawer-bound', '');

    var lastFocused = null;
    var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function open() {
      lastFocused = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      if (overlay) {
        overlay.classList.add('is-open');
        overlay.setAttribute('aria-hidden', 'false');
      }
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      var focusables = drawer.querySelectorAll(FOCUSABLE);
      if (focusables.length) focusables[0].focus();
    }

    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (overlay) {
        overlay.classList.remove('is-open');
        overlay.setAttribute('aria-hidden', 'true');
      }
      if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    if (openBtn) openBtn.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (overlay) overlay.addEventListener('click', close);

    drawer.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var focusables = Array.prototype.slice.call(drawer.querySelectorAll(FOCUSABLE));
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    // The newsletter form is a real customer form: on submit the page reloads,
    // so re-open the drawer when it comes back with a success/error message.
    if (drawer.querySelector('[role="status"], [role="alert"]')) open();

    // Auto-open after the configured delay, on every visit (matches the mockup).
    // Skipped in the theme editor so it doesn't pop while a merchant edits.
    var inEditor = window.Shopify && window.Shopify.designMode;
    if (!inEditor && drawer.hasAttribute('data-footer-drawer-autoopen')) {
      var delay = parseInt(drawer.getAttribute('data-footer-drawer-autoopen'), 10);
      if (isNaN(delay)) delay = 5;
      setTimeout(function () {
        if (!drawer.classList.contains('is-open')) open();
      }, delay * 1000);
    }
  }

  /* Header search drawer: slides in from the right, replacing Dawn's search
     modal. Live results come from the nested <predictive-search> component; this
     handles open/close, focus trapping, and the suggestion chips (a chip fills
     the input and dispatches `input` so predictive search fires). */
  // The trigger (header section) and overlay (search-drawer section) live in
  // separate sections, so trigger clicks + Escape are handled by document-level
  // delegation bound once, while these refs point at the current overlay.
  var searchOpen = null;
  var searchClose = null;
  var searchDelegationBound = false;

  function searchDrawerInit(root) {
    var overlay = root.querySelector('[data-search-overlay]:not([data-search-bound])');
    if (!overlay) return;

    // The overlay renders inside the sticky header group, which is its own
    // stacking context (position:sticky; z-index:3) — a fixed overlay there is
    // capped at z-index 3 and paints under page content. Relocate it to <body>
    // so its z-index sits in the root context. Drop any overlay left by a
    // previous theme-editor reload first so we don't stack duplicates.
    document.querySelectorAll('[data-search-relocated]').forEach(function (el) { el.remove(); });
    overlay.setAttribute('data-search-bound', '');
    overlay.setAttribute('data-search-relocated', '');
    document.body.appendChild(overlay);

    var closers = overlay.querySelectorAll('[data-search-close]');
    var input = overlay.querySelector('.search-drawer__input');
    var lastFocused = null;
    var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function setExpanded(value) {
      document.querySelectorAll('[data-search-trigger]').forEach(function (t) {
        t.setAttribute('aria-expanded', value);
      });
    }

    function open() {
      lastFocused = document.activeElement;
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      setExpanded('true');
      document.body.style.overflow = 'hidden';
      setTimeout(function () { if (input) input.focus(); }, 60);
    }

    function close() {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      setExpanded('false');
      document.body.style.overflow = '';
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    searchOpen = open;
    searchClose = close;

    closers.forEach(function (el) { el.addEventListener('click', close); });

    overlay.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var focusables = Array.prototype.slice
        .call(overlay.querySelectorAll(FOCUSABLE))
        .filter(function (el) { return el.offsetParent !== null; });
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    overlay.querySelectorAll('[data-search-chip]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        if (!input) return;
        // Focus while still empty so predictive search's onFocus bails; the
        // dispatched input event then drives a single search for the chip term.
        input.focus();
        input.value = chip.textContent.trim();
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });
    });

    if (!searchDelegationBound) {
      searchDelegationBound = true;
      document.addEventListener('click', function (e) {
        if (e.target.closest('[data-search-trigger]')) {
          e.preventDefault();
          if (searchOpen) searchOpen();
        }
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && searchClose && document.querySelector('[data-search-overlay].is-open')) {
          searchClose();
        }
      });
    }
  }

  /* Mobile menu drawer: slides in from the right. Open/close + backdrop + Esc,
     a focus trap, and accordion submenus (data-drawer-toggle reveals its
     [aria-controls] panel). Replaces Dawn's left menu-drawer. */
  function mobileDrawerInit(root) {
    var drawer = root.querySelector('[data-drawer]:not([data-drawer-bound])');
    if (!drawer) return;
    drawer.setAttribute('data-drawer-bound', '');

    var trigger = root.querySelector('[data-drawer-trigger]');
    var backdrop = root.querySelector('[data-drawer-backdrop]');
    var closeBtn = drawer.querySelector('[data-drawer-close]');
    var lastFocused = null;
    var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function open() {
      lastFocused = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      if (backdrop) {
        backdrop.classList.add('is-visible');
        backdrop.setAttribute('aria-hidden', 'false');
      }
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (backdrop) {
        backdrop.classList.remove('is-visible');
        backdrop.setAttribute('aria-hidden', 'true');
      }
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    if (trigger) trigger.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (backdrop) backdrop.addEventListener('click', close);

    drawer.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var focusables = Array.prototype.slice
        .call(drawer.querySelectorAll(FOCUSABLE))
        .filter(function (el) { return el.offsetParent !== null; });
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });

    drawer.querySelectorAll('[data-drawer-toggle]').forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var id = toggle.getAttribute('aria-controls');
        var panel = id ? drawer.querySelector('#' + (window.CSS && CSS.escape ? CSS.escape(id) : id)) : null;
        var isOpen = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!isOpen));
        if (!panel) return;
        if (isOpen) panel.setAttribute('hidden', '');
        else panel.removeAttribute('hidden');
      });
    });
  }

  /* PDP gallery — thumbnail click swaps the main image. Scoped per
     [data-product-gallery]; the main <img> is matched by class within. */
  function productGalleryInit(root) {
    root.querySelectorAll('[data-product-gallery]:not([data-product-gallery-bound])').forEach(function (gallery) {
      gallery.setAttribute('data-product-gallery-bound', '');
      var mainImg = gallery.querySelector('.product-gallery__main-img');
      var thumbs = gallery.querySelectorAll('[data-product-thumb]');
      if (!mainImg || !thumbs.length) return;

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var src = thumb.getAttribute('data-src');
          if (!src) return;
          mainImg.removeAttribute('srcset');
          mainImg.src = src;
          mainImg.alt = thumb.getAttribute('data-alt') || mainImg.alt;
          thumbs.forEach(function (t) { t.classList.remove('is-active'); });
          thumb.classList.add('is-active');
        });
      });
    });
  }

  /* PDP sticky quote bar — slides up once the hero scrolls out of view.
     CSS hides it below 1024px, so toggling the class there is a no-op. */
  function productStickyInit(root) {
    root.querySelectorAll('[data-product-sticky]:not([data-product-sticky-bound])').forEach(function (bar) {
      bar.setAttribute('data-product-sticky-bound', '');
      var hero = document.querySelector('.product-hero');
      if (!hero || !('IntersectionObserver' in window)) return;
      bar.removeAttribute('hidden');
      var io = new IntersectionObserver(function (entries) {
        bar.classList.toggle('is-visible', !entries[0].isIntersecting);
      });
      io.observe(hero);
    });
  }

  /* Collection PLP filtering — upgrades the server-rendered facet chip links to
     instant in-place rendering via the Section Rendering API. Chips stay real
     links, so this is pure progressive enhancement (full reload if JS/fetch
     fails). Bound once globally via delegation, so the innerHTML swap needs no
     re-bind. */
  function collectionFilterInit() {
    if (window.__ceasCollectionFilterBound) return;
    if (!document.querySelector('[data-collection-grid]')) return;
    window.__ceasCollectionFilterBound = true;

    function render(url, push) {
      var wrap = document.querySelector('[data-collection-grid]');
      if (!wrap) { window.location.href = url; return; }
      var id = wrap.getAttribute('data-collection-grid');
      var sep = url.indexOf('?') > -1 ? '&' : '?';
      wrap.classList.add('is-loading');
      fetch(url + sep + 'section_id=' + encodeURIComponent(id))
        .then(function (res) {
          if (!res.ok) throw new Error('Bad response');
          return res.text();
        })
        .then(function (html) {
          var fresh = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('[data-collection-grid]');
          if (!fresh) { window.location.href = url; return; }
          wrap.replaceWith(fresh);
          if (push) window.history.pushState({ ceasColl: true }, '', url);
        })
        .catch(function () { window.location.href = url; });
    }

    document.addEventListener('click', function (e) {
      var chip = e.target.closest && e.target.closest('.collection-chip');
      if (!chip || !chip.closest('[data-collection-grid]')) return;
      var href = chip.getAttribute('href');
      if (!href) return;
      e.preventDefault();
      render(href, true);
    });

    window.addEventListener('popstate', function () {
      if (document.querySelector('[data-collection-grid]')) {
        render(window.location.href, false);
      }
    });
  }

  function initAll(root) {
    root = root || document;
    collectionFilterInit();
    revealInit(root);
    heroRevealInit(root);
    headingRevealInit(root);
    recommendationsRevealInit(root);
    videoRevealInit(root);
    videoEmbedInit(root);
    tabsInit(root);
    carouselInit(root);
    categoryInit(root);
    quoteInit(root);
    quoteModalInit(root);
    serialInit(root);
    stepperInit(root);
    productGalleryInit(root);
    productStickyInit(root);
    footerAccordionInit(root);
    footerDrawerInit(root);
    searchDrawerInit(root);
    mobileDrawerInit(root);
  }

  function start() {
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      gsap.registerPlugin(ScrollTrigger);
    }
    initAll(document);
  }

  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);

  // Theme editor: re-init only the reloaded section's subtree.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
